'use client';

// Identity service for user identification and property management
import posthog from 'posthog-js';
import { setUserProperties, setUserPropertiesOnce, setGroup } from '@/lib/posthog';

// User properties schema
export interface UserProperties {
  // Core Identity
  email?: string;
  name?: string;
  created_at?: string;

  // Onboarding State
  onboarding_complete?: boolean;
  intake_complete?: boolean;
  goals_complete?: boolean;
  onboarding_completed_at?: string;

  // Case Profile
  situation_type?: string;
  complexity_level?: string;
  state?: string;
  property_system?: string;
  has_children?: boolean;
  children_count?: number;
  has_business?: boolean;
  has_real_estate?: boolean;
  has_retirement_accounts?: boolean;
  has_prenup?: boolean;

  // Subscription
  tier?: string;
  subscription_status?: string;
  mrr?: number;
  subscription_start_date?: string;
  trial_end_date?: string;
  lifetime_value?: number;

  // Engagement
  strategies_generated?: number;
  documents_uploaded?: number;
  last_active_at?: string;
  days_since_signup?: number;
  session_count?: number;

  // Attribution
  initial_referrer?: string;
  initial_utm_source?: string;
  initial_utm_medium?: string;
  initial_utm_campaign?: string;
  signup_source?: string;
}

// Subscription info interface
export interface SubscriptionInfo {
  tier: 'free' | 'core' | 'premium' | 'professional';
  status?: string;
  created_at?: string;
  trial_end?: string;
}

// Profile interface (partial)
export interface ProfileInfo {
  id: string;
  email?: string;
  full_name?: string;
  onboarding_complete?: boolean;
  intake_complete?: boolean;
  goals_complete?: boolean;
  situation_type?: string;
  complexity_level?: string;
  state?: string;
  children_count?: number;
  has_business?: boolean;
  has_real_estate?: boolean;
  has_retirement_accounts?: boolean;
  has_prenup?: boolean;
  created_at?: string;
}

// Tier pricing for MRR calculation
const TIER_PRICING: Record<string, number> = {
  free: 0,
  core: 29,
  premium: 79,
  professional: 149,
};

export const identityService = {
  /**
   * Identify user on login/signup
   */
  identifyUser(userId: string, email: string, metadata?: Record<string, unknown>) {
    if (typeof window === 'undefined') return;

    posthog.identify(userId, {
      email,
      name: metadata?.name || metadata?.full_name,
      plan: metadata?.plan || metadata?.subscription_plan || 'trial',
      user_type: metadata?.user_type,
      state: metadata?.state,
      company: metadata?.company_name,
      created_at: metadata?.created_at || new Date().toISOString(),
      ...metadata,
    });

    // Set initial properties that should only be set once
    setUserPropertiesOnce({
      first_seen_at: new Date().toISOString(),
      initial_referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    });

    // Capture UTM parameters if present
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');

      if (utmSource || utmMedium || utmCampaign) {
        setUserPropertiesOnce({
          initial_utm_source: utmSource,
          initial_utm_medium: utmMedium,
          initial_utm_campaign: utmCampaign,
        });
      }
    }
  },

  /**
   * Update user properties after onboarding
   */
  updateOnboardingProperties(profile: ProfileInfo) {
    const properties: UserProperties = {
      // Core identity - always update these for visibility in PostHog
      name: profile.full_name,
      email: profile.email,
      // Onboarding state
      onboarding_complete: profile.onboarding_complete,
      intake_complete: profile.intake_complete,
      goals_complete: profile.goals_complete,
      situation_type: profile.situation_type,
      complexity_level: profile.complexity_level,
      state: profile.state,
      has_children: (profile.children_count || 0) > 0,
      children_count: profile.children_count || 0,
      has_business: profile.has_business || false,
      has_real_estate: profile.has_real_estate || false,
      has_retirement_accounts: profile.has_retirement_accounts || false,
      has_prenup: profile.has_prenup || false,
    };

    // Remove undefined values
    const cleanProperties = Object.fromEntries(
      Object.entries(properties).filter(([, v]) => v !== undefined)
    );

    setUserProperties(cleanProperties);

    // Set onboarding completed timestamp once
    if (profile.onboarding_complete) {
      setUserPropertiesOnce({
        onboarding_completed_at: new Date().toISOString(),
      });
    }
  },

  /**
   * Update subscription properties
   */
  updateSubscriptionProperties(userId: string, subscription: SubscriptionInfo) {
    const mrr = TIER_PRICING[subscription.tier] || 0;

    setUserProperties({
      tier: subscription.tier,
      subscription_status: subscription.status || 'none',
      mrr,
      subscription_start_date: subscription.created_at,
      trial_end_date: subscription.trial_end,
    });

    // Set purchased_at once (first time user upgrades to a paid tier)
    if (subscription.tier !== 'free' && subscription.created_at) {
      setUserPropertiesOnce({
        purchased_at: subscription.created_at,
      });

      // Compute days_to_upgrade from first_seen_at to purchased_at
      // This requires reading first_seen_at which was set on signup
      try {
        const firstSeen = posthog.get_property('$initial_person_info')?.r ||
          posthog.get_property('first_seen_at');
        if (firstSeen) {
          const daysToUpgrade = Math.round(
            (new Date(subscription.created_at).getTime() - new Date(firstSeen).getTime()) /
            (1000 * 60 * 60 * 24)
          );
          setUserProperties({ days_to_upgrade: Math.max(0, daysToUpgrade) });
        }
      } catch {
        // Silently fail — person properties may not be available yet
      }
    }

    // Set subscription tier as a group for revenue attribution
    setGroup('subscription_tier', subscription.tier, {
      price: mrr,
    });
  },

  /**
   * Update engagement metrics
   */
  updateEngagementMetrics(metrics: {
    strategies_generated?: number;
    documents_uploaded?: number;
  }) {
    setUserProperties({
      ...metrics,
      last_active_at: new Date().toISOString(),
    });

    // Set first strategy timestamp once
    if (metrics.strategies_generated && metrics.strategies_generated > 0) {
      setUserPropertiesOnce({
        first_strategy_generated_at: new Date().toISOString(),
      });
    }

    // Set first document timestamp once
    if (metrics.documents_uploaded && metrics.documents_uploaded > 0) {
      setUserPropertiesOnce({
        first_document_uploaded_at: new Date().toISOString(),
      });
    }
  },

  /**
   * Set signup source
   */
  setSignupSource(source: 'organic' | 'paid' | 'referral' | 'direct') {
    setUserPropertiesOnce({
      signup_source: source,
    });
  },

  /**
   * Increment a user metric
   */
  incrementMetric(metric: string, value: number = 1) {
    if (typeof window === 'undefined') return;

    // PostHog doesn't have a native increment, so we track via events
    // The actual increment should be done server-side or via person properties API
    posthog.capture('metric_incremented', {
      metric,
      increment_value: value,
    });
  },

  /**
   * Reset user identity on logout
   */
  resetUser() {
    if (typeof window === 'undefined') return;
    posthog.reset();
  },
};

export default identityService;
